import config from '../../../config';
import {authFetch, checkStatus} from '../../utils/http';
import {getUtcOffset, getURIEncodedTimeZone} from '../../utils/datetime';

export default class LiveDocumentService {
  search(organisationId) {
    return authFetch(`${config.documentApi}/api/v1/documents?organisationId=${organisationId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  searchByClient(organisationId, clientId) {
    return authFetch(`${config.documentApi}/api/v1/documents?organisationId=${organisationId}&clientId=${clientId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  searchOfSubcontractor(subcontractorId, organisationId) {
    return authFetch(`${config.documentApi}/api/v1/documents/subcontractors/${subcontractorId}?organisationId=${organisationId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  searchByProject(interventionId) {
    return authFetch(`${config.documentApi}/api/v1/documents?interventionId=${interventionId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  searchByProjectOfSubcontractor(interventionId, subcontractorId, organisationId) {
    return authFetch(`${config.documentApi}/api/v1/documents?interventionId=${interventionId}&subcontractorId=${subcontractorId}&organisationId=${organisationId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  getDossierState(organisationId, country, signal) {
    return authFetch(`${config.documentApi}/api/v1/dossiers/states?organisationId=${organisationId}&country=${country}`, {signal})
      .then(checkStatus)
      .then(response => response.json());
  }

  getDossierStateDetails(organisationId, country) {
    return authFetch(`${config.documentApi}/api/v1/dossiers/states/details?organisationId=${organisationId}&country=${country}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  getDossierStateForGlobalDashboard(organisationId, signal) {
    return authFetch(`${config.documentApi}/api/v1/dossiers/states/all?organisationId=${organisationId}`, {signal})
      .then(checkStatus)
      .then(response => response.json());
  }

  getInterventionDossierState(interventionId, signal) {
    return authFetch(`${config.documentApi}/api/v1/dossiers/states?interventionId=${interventionId}`, {signal})
      .then(response => checkStatus(response, false))
      .then(response => response.json());
  }

  getFileUrl(documentId, excel, interventionId = null) {
    let interventionParam;
    if (excel) {
      interventionParam = (interventionId === null ? '' : `&interventionId=${interventionId}`);
      return `${config.documentApi}/api/v1/documents/${documentId}/file?excel=true${interventionParam}`;
    }

    interventionParam = (interventionId === null ? '' : `?interventionId=${interventionId}`);
    return `${config.documentApi}/api/v1/documents/${documentId}/file${interventionParam}`;
  }

  getFileProofUrl(documentUid, interventionId = null) {
    const interventionParam = (interventionId === null ? '' : `?interventionId=${interventionId}`);
    return `${config.documentApi}/api/v1/documents/${documentUid}/proof${interventionParam}`;
  }

  getDossierPdfUrl(organisationId, country) {
    return `${config.documentApi}/api/v1/dossiers/pdf?organisationId=${organisationId}&country=${country}`;
  }

  getDossierZipUrl(organisationId, country) {
    return `${config.documentApi}/api/v1/dossiers/zip?organisationId=${organisationId}&utcOffset=${getUtcOffset()}&country=${country}`;
  }

  getDossierArchivesZipUrl(organisationId) {
    return `${config.documentApi}/api/v1/dossiers/archives/zip?organisationId=${organisationId}&utcOffset=${getUtcOffset()}`;
  }

  getDossierForClientPdfUrl(organisationId, clientId) {
    return `${config.documentApi}/api/v1/dossiers/pdf?organisationId=${organisationId}&clientId=${clientId}`;
  }

  getDossierForClientZipUrl(organisationId, clientId) {
    return `${config.documentApi}/api/v1/dossiers/zip?organisationId=${organisationId}&clientId=${clientId}&utcOffset=${getUtcOffset()}`;
  }

  getDossierOfSubcontractorPdfUrl(organisationId, subcontractorId, country) {
    return `${config.documentApi}/api/v1/dossiers/pdf?organisationId=${organisationId}&subcontractorId=${subcontractorId}&country=${country}`;
  }

  getDossierOfSubcontractorZipUrl(organisationId, subcontractorId, country) {
    return `${config.documentApi}/api/v1/dossiers/zip?organisationId=${organisationId}&subcontractorId=${subcontractorId}&utcOffset=${getUtcOffset()}&country=${country}`;
  }

  getDossierArchivesOfSubcontractorZipUrl(organisationId, subcontractorId) {
    return `${config.documentApi}/api/v1/dossiers/archives/zip?organisationId=${organisationId}&subcontractorId=${subcontractorId}&utcOffset=${getUtcOffset()}`;
  }

  getProjectDossierPdfUrl(interventionId, locale) {
    return `${config.documentApi}/api/v1/dossiers/pdf?interventionId=${interventionId}&locale=${locale}`;
  }

  getProjectDossierZipUrl(interventionId, locale) {
    return `${config.documentApi}/api/v1/dossiers/zip?interventionId=${interventionId}&utcOffset=${getUtcOffset()}&locale=${locale}`;
  }

  getProjectDossierOfSubcontractorPdfUrl(interventionId, subcontractorId, organisationId, locale) {
    return `${config.documentApi}/api/v1/dossiers/pdf?interventionId=${interventionId}&subcontractorId=${subcontractorId}&organisationId=${organisationId}&locale=${locale}`;
  }

  getProjectDossierOfSubcontractorZipUrl(interventionId, subcontractorId, organisationId, locale) {
    return `${config.documentApi}/api/v1/dossiers/zip?interventionId=${interventionId}&subcontractorId=${subcontractorId}&utcOffset=${getUtcOffset()}&organisationId=${organisationId}&locale=${locale}`;
  }

  downloadDossierOverview(organisationId, locale, country) {
    return `${config.documentApi}/api/v1/dossiers/overview?organisationId=${organisationId}&country=${country}&locale=${locale}&timeZone=${getURIEncodedTimeZone()}`;
  }

  downloadDossierOverviewProject(interventionId, locale) {
    return `${config.documentApi}/api/v1/dossiers/overview?interventionId=${interventionId}&locale=${locale}&timeZone=${getURIEncodedTimeZone()}`;
  }

  getProjectOverviewReportDownloadUrl(interventionId, locale) {
    return `${config.documentApi}/api/v1/reports/collector?interventionId=${interventionId}&locale=${locale}&timeZone=${getURIEncodedTimeZone()}`;
  }

  getExportUrl(exportId) {
    return `${config.documentApi}/api/v1/documents/exports/${exportId}`;
  }

  getNewVersionToSign(documentId) {
    return authFetch(`${config.documentApi}/api/v1/documents/${documentId}/new-version-to-sign`)
      .then(checkStatus)
      .then(response => response.json());
  }

  submitNewDocument(uid, file, info) {
    const formData = new FormData();
    formData.append('info', JSON.stringify(info));
    formData.append('file', file);

    return authFetch(`${config.documentApi}/api/v1/documents/${uid}`, {
      method: 'PUT',
      body: formData
    })
      .then(checkStatus);
  }

  // Create a new occurence of a document
  duplicateDocument(documentId) {
    const document = {
      documentId
    };
    return authFetch(`${config.documentApi}/api/v1/documents/duplicate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(document)
    })
      .then(checkStatus);
  }

  updateAdditionalDesignation(uid, additionalDesignation) {
    return authFetch(`${config.documentApi}/api/v1/documents/${uid}/additionalDesignation`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({additionalDesignation})
    })
      .then(checkStatus);
  }

  deleteDocument(organisationId, document) {
    return authFetch(`${config.documentApi}/api/v1/documents/${document.uid}?organisationId=${organisationId}`, {
      method: 'DELETE'
    }).then(checkStatus);
  }

  findOne(documentId) {
    return authFetch(`${config.documentApi}/api/v1/documents/${documentId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  getDossierCountPerState(organisationId, projectId) {
    return authFetch(`${config.documentApi}/api/v1/dossiers/${organisationId}/${projectId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  getDocumentsCountPerState(organisationId, projectId) {
    return authFetch(`${config.documentApi}/api/v1/documents/${organisationId}/${projectId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  getDocumentsAndDossierCountPerStateByUser(userId) {
    return authFetch(`${config.documentApi}/api/v1/states?userId=${userId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  getArchivedDocuments(organisationId) {
    return authFetch(`${config.documentApi}/api/v1/archives?organisationId=${organisationId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  getArchivedDocumentsOfSubcontractor(subcontractorId, organisationId) {
    return authFetch(`${config.documentApi}/api/v1/archives?organisationId=${organisationId}&subcontractorId=${subcontractorId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  getDocumentArchivesByIntervention(interventionId, organisationId, correlationId) {
    return authFetch(`${config.documentApi}/api/v1/archives?interventionId=${interventionId}&organisationId=${organisationId}&correlationId=${correlationId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  getProjectSharedDocuments(projectId, organisationId) {
    return authFetch(`${config.documentApi}/api/v1/documents/shared/projects/${projectId}?organisationId=${organisationId}`)
      .then(response => checkStatus(response, false))
      .then(response => response.json());
  }

  submitProjectSharedDocument(projectId, file, info, document) {
    let url = `${config.documentApi}/api/v1/documents/shared/projects/${projectId}`;
    if (document && document.uid) {
      url += `/${document.uid}`;
    }

    const formData = new FormData();
    formData.append('info', JSON.stringify(info));
    formData.append('file', file);

    return authFetch(url, {
      method: 'POST',
      body: formData
    })
      .then(checkStatus);
  }

  createDocumentRemindersRequest(data) {
    return authFetch(`${config.documentApi}/api/v1/document-reminders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(checkStatus);
  }

  createDocumentExport(data) {
    return authFetch(`${config.documentApi}/api/v1/documents/exports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(checkStatus);
  }

  addDocument(organisationId, documentType) {
    const addDocument = {
      organisationId,
      subtypeCode: documentType.code
    };
    return authFetch(`${config.documentApi}/api/v1/documents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(addDocument)
    })
      .then(checkStatus);
  }

  requireProjectSpecificDocument(projectId, subtype, organisationId) {
    return authFetch(`${config.documentApi}/api/v1/documents/shared/projects/${projectId}/specific?subtype-code=${subtype}&organisationId=${organisationId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(checkStatus);
  }

  getAllDocumentValidVersions(organisationId, correlationId) {
    return authFetch(`${config.documentApi}/api/v1/archives?organisationId=${organisationId}&correlationId=${correlationId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  getAllDocumentValidVersionsBySubcontractor(organisationId, subcontractorId, correlationId) {
    return authFetch(`${config.documentApi}/api/v1/archives?organisationId=${organisationId}&subcontractorId=${subcontractorId}&correlationId=${correlationId}`)
      .then(checkStatus)
      .then(response => response.json());
  }
}
